/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, TrendingUp, ArrowUp, ArrowDown, Sparkles } from 'lucide-react';
import MentorshipPairingCard from '@/components/terra/MentorshipPairingCard';

/**
 * Weekly Movement Digest™ — calm summary across all of Phase 3.
 *
 * Aggregates: perception movements, stuck cohorts, top events by impact,
 * depth-mix shift, right-subscriber emergence, referral movement, counselor
 * bottleneck signals, ecosystem resistance, and a deterministic "recommended
 * weekly focus" derived from the data.
 *
 * Mentorship pairings are folded in here as a separate calm section.
 */
export default function MovementDigestPage() {
  const [digest, setDigest] = useState(null);
  const [pairings, setPairings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/terra-growth/intelligence/movement-digest?days=${days}`),
      axios.get(`${API}/terra-growth/intelligence/mentorship-pairings?days=180`),
    ])
      .then(([d, p]) => { setDigest(d.data); setPairings(p.data); })
      .catch(() => { setDigest(null); setPairings(null); })
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="movement-digest-page" className="max-w-7xl mx-auto px-6 py-8">
        <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
            <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Weekly Movement Digest™</h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
              A calm, deterministic snapshot of perception movement across the territory —
              what shifted, what's holding, where to lean in this week.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 14, 30].map(d => (
              <button key={d} data-testid={`digest-days-${d}`} onClick={() => setDays(d)}
                      className={`text-[11px] rounded-full px-3 py-1 border ${
                        days === d ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' : 'bg-white text-[#5C5955] border-[#D8D4CE]'}`}>
                last {d}d
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Composing digest…
          </div>
        ) : !digest ? (
          <div data-testid="movement-digest-error" className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            Could not compose digest right now.
          </div>
        ) : (
          <div className="space-y-6">
            <RecommendedFocus focus={digest.recommended_focus} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Perception movements" value={digest.perception_movements.total}
                    sub={`↑ ${digest.perception_movements.forwards}  ↓ ${digest.perception_movements.regressions}`} />
              <Stat label="Stuck cohorts" value={digest.stuck_cohorts.total} sub="leads needing movement" />
              <Stat label="Right-subscriber emergence" value={digest.right_subscriber_emerged} sub="entered SUBSCRIBER state" />
              <Stat label="Referral movement" value={digest.referral_movement} sub="new referred leads" />
            </div>
            <TopEdges data={digest.perception_movements.top_edges} />
            <TopEvents data={digest.top_events_by_impact} />
            <DepthShift data={digest.depth_mix_shift} />
            <BottleneckEcosystem
              bottleneck={digest.counselor_bottleneck_signals}
              weakest={digest.weakest_ecosystem}
              stuckByState={digest.stuck_cohorts.by_state}
            />
            {pairings && pairings.pairings.length > 0 && (
              <MentorshipSection pairings={pairings} />
            )}
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function RecommendedFocus({ focus }) {
  return (
    <section data-testid="digest-focus"
             className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1]/50 p-5">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8C6239] mb-2">Recommended focus this week</p>
      <ul className="space-y-1.5">
        {focus.map((f, i) => (
          <li key={i} data-testid={`digest-focus-${i}`} className="flex items-start gap-2 text-sm text-[#5C3D1F]">
            <span className="w-1 h-1 rounded-full bg-[#8C6239] mt-2" /> {f}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Stat({ label, value, sub }) {
  return (
    <article className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">{label}</p>
      <p className="text-3xl font-['Outfit'] tabular-nums text-[#2C2A29] mt-1">{value}</p>
      <p className="text-[10px] text-[#8A8681] mt-0.5">{sub}</p>
    </article>
  );
}

function TopEdges({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <section data-testid="digest-top-edges" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-2 flex items-center gap-1">
        <TrendingUp className="w-3 h-3" /> Top movements (from → to)
      </p>
      <ul className="space-y-1">
        {data.map((e, i) => (
          <li key={i} className="flex items-center gap-2 text-[12px]">
            <span className="text-[#5C5955] uppercase tracking-[0.14em] text-[10px]">{e.from_state}</span>
            <ArrowUp className="w-3 h-3 text-emerald-600" />
            <span className="text-[#2C2A29] uppercase tracking-[0.14em] text-[10px]">{e.to_state}</span>
            <span className="ml-auto tabular-nums text-[#5C5955]">{e.count}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TopEvents({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <section data-testid="digest-top-events" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-2">Top events by Perception Impact</p>
      <ul className="space-y-1">
        {data.map(e => (
          <li key={e.id} className="flex items-center justify-between gap-2 text-[12px]">
            <span className="text-[#2C2A29] truncate">{e.title || e.event_type}</span>
            <span className="tabular-nums text-emerald-700">{e.impact_composite}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DepthShift({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <section data-testid="digest-depth-shift" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-2">Conversation depth mix · week-over-week</p>
      <ul className="space-y-1">
        {data.map(r => (
          <li key={r.depth_code} className="flex items-center justify-between gap-2 text-[12px]"
              data-testid={`depth-shift-${r.depth_code}`}>
            <span className="text-[#5C5955] uppercase tracking-[0.14em] text-[10px]">{r.depth_code}</span>
            <span className="tabular-nums text-[#2C2A29]">{r.current}</span>
            <span className={`text-[10px] inline-flex items-center gap-0.5 tabular-nums ${
              r.delta > 0 ? 'text-emerald-700' : r.delta < 0 ? 'text-stone-600' : 'text-[#8A8681]'}`}>
              {r.delta > 0 ? <ArrowUp className="w-3 h-3" /> : r.delta < 0 ? <ArrowDown className="w-3 h-3" /> : null}
              {r.delta >= 0 ? '+' : ''}{r.delta}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BottleneckEcosystem({ bottleneck, weakest, stuckByState }) {
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div data-testid="digest-bottlenecks" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-2">Counselor bottleneck signals</p>
        <p className="text-2xl font-['Outfit'] text-[#2C2A29] tabular-nums">{bottleneck}</p>
        <p className="text-[10px] text-[#8A8681] mt-0.5">rungs needing mentorship support</p>
      </div>
      <div data-testid="digest-weakest-eco" className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-2">Slowest ecosystem this window</p>
        {weakest ? (
          <>
            <p className="text-base text-[#2C2A29] truncate">{weakest.ecosystem_code}</p>
            <p className="text-[10px] text-[#8A8681] mt-0.5">{weakest.velocity_pct}% velocity · {weakest.sample_count} transitions</p>
          </>
        ) : (
          <p className="text-[11px] text-[#B5B0A8]">No clear bottleneck — signal is calm.</p>
        )}
        {Object.keys(stuckByState).length > 0 && (
          <p className="text-[10px] text-[#8A8681] mt-2">
            Stuck states: {Object.entries(stuckByState).map(([k, v]) => `${k}(${v})`).join(' · ')}
          </p>
        )}
      </div>
    </section>
  );
}

function MentorshipSection({ pairings }) {
  return (
    <section data-testid="digest-mentorship">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8C6239] mb-3 flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Suggested Mentorship Pairings™
      </p>
      <p className="text-[11px] text-[#8A8681] mb-3 italic max-w-3xl">{pairings.framing_note}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {pairings.pairings.slice(0, 6).map((p, i) => (
          <MentorshipPairingCard key={i} pairing={p} />
        ))}
      </div>
    </section>
  );
}
